import { computed } from 'vue-demi';
import useAlertManager from '@/platform/composables/useAlertManager';
import useLegacyConnector from '@/platform/composables/useLegacyConnector';
import useModalManager from '@/platform/composables/useModalManager';
import useRouteManager from '@/platform/composables/useRouteManager';
import useEventsManager from '@/platform/composables/useEventsManager';
import useCurrentUser from '@/platform/composables/useCurrentUser';
import useCurrentUserLocalization from '@/platform/composables/useCurrentUserLocalization';
import useCurrentUserPermissions from '@/platform/composables/useCurrentUserPermissions';
import useUserGroupPicker from '@/platform/composables/useUserGroupPicker';
import useRealTimeUpdates from '@/platform/composables/useRealTimeUpdates';
import useLogger from '@/platform/composables/useLogger';
import useAccount from '@/platform/composables/useAccount';
import usePendo from '@/platform/composables/usePendo';
import useFeatures from '@/platform/composables/useFeatures';
import useCheckout from '@/platform/composables/useCheckout';

export default function useAppFacade({ context }) {
  const logger = useLogger();
  const alertManager = useAlertManager({ logger });
  const legacyConnector = useLegacyConnector({ logger });
  const eventsManager = useEventsManager({ legacyConnector, logger });
  const routeManager = useRouteManager({ context, legacyConnector, logger });
  const modalManager = useModalManager({ routeManager, legacyConnector, logger });
  const accountContext = useAccount();
  const userContext = useCurrentUser({ logger });
  const userGroupPicker = useUserGroupPicker({ modalManager, logger });
  const userPermissions = useCurrentUserPermissions({ userContext });
  const userLocalization = useCurrentUserLocalization({ userContext });
  const realTimeUpdates = useRealTimeUpdates({ logger, userContext, accountContext });
  const pendoContext = usePendo({ userContext, accountContext });
  const featuresContext = useFeatures({ userLocalization, userContext });
  const checkout = useCheckout({});
  const isAppReady = computed(() => userContext.user.value && accountContext.account.value);

  // Print if the following overrides are enabled
  logger.logStatus('`@teamwork/ui-kit` override enabled', process.env.VUE_APP_TEAMWORK_UI_KIT);
  logger.logStatus(
    '`@teamwork/ui-kit-vuetify` override enabled',
    process.env.VUE_APP_TEAMWORK_UI_KIT_VUETIFY,
  );
  logger.logStatus('`@teamwork/fetcher` override enabled', process.env.VUE_APP_TEAMWORK_FETCHER);
  logger.logStatus('`@teamwork/use` override enabled', process.env.VUE_APP_TEAMWORK_USE);

  logger.logInfo('Starting App Facade');

  return {
    // Event related api
    eventsManager,
    // Alerts
    alertManager,
    // TBD Modal et al, current project etc.....,
    // logger
    logger,
    modalManager,
    checkout,
    // All the route related context
    routeManager,
    legacyConnector,
    userContext,
    userLocalization,
    userPermissions,
    userGroupPicker,
    pendoContext,
    accountContext,
    featuresContext,
    realTimeUpdates,
    isAppReady,
  };
}
